import React from "react";
import { Icon } from "../../UIElements/Icon";
import { Button } from "../../UIElements/Button";
import { useGlobalUtils } from "../../../../lib/globalUtils";
import { useTranslation } from "react-i18next";
import {
  STATUS_LABELS,
  STATUS_BADGE_COLORS,
  CHANNEL_ICONS,
  CHANNEL_COLORS,
  getStatusGroup,
  getCustomerName,
  getCustomerPhone,
} from "@finitless/shared/orderConstants";
import {
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
  HoverCardArrow,
} from "../../UIElements/HoverCard";

const OrderCard = ({
  order,
  onViewDetails,
  onViewConversation,
  statusLabels,
  getStatusGroup: getStatusGroupProp,
}) => {
  const { t } = useTranslation("orders");
  const { parsePrice, calculateEstimatedDelivery, parseDate } =
    useGlobalUtils();

  const getStatusBadge = (status) => {
    const statusGroup = getStatusGroupProp
      ? getStatusGroupProp(status)
      : getStatusGroup(status);
    const fallbackStatusLabel = statusLabels
      ? statusLabels[status]
      : STATUS_LABELS[status] || status;
    const statusLabel = t(`statusLabels.${status}`, {
      defaultValue: fallbackStatusLabel,
    });

    return (
      <span
        className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
          STATUS_BADGE_COLORS[statusGroup] || STATUS_BADGE_COLORS.unknown
        }`}
        title={statusLabel}
      >
        <span className="block max-w-[140px] truncate">{statusLabel}</span>
      </span>
    );
  };

  const ChannelIconName = CHANNEL_ICONS[order.channel] || "MessageSquare";
  const orderTime = parseDate(order.created_at, { useUTC: true });
  const estimatedDeliveryTime = parseDate(calculateEstimatedDelivery(order));
  const showEstimatedDelivery = estimatedDeliveryTime !== orderTime;

  return (
    <div className="border-border bg-background hover:bg-card/50 flex h-full flex-col rounded-lg border p-3 transition-colors sm:p-4">
      {/* Header */}
      <div className="mb-3">
        <div className="flex items-start space-x-2">
          <div
            className={`bg-card rounded-lg p-1 sm:p-1.5 ${
              CHANNEL_COLORS[order.channel] || "text-muted-foreground"
            } flex-shrink-0`}
          >
            <Icon name={ChannelIconName} className="h-3 w-3 sm:h-4 sm:w-4" />
          </div>
          <div className="min-w-0 flex-1">
            {/* Order Title */}
            <h3 className="text-foreground mb-1.5 text-xs font-semibold sm:text-sm">
              {t("card.orderNumber", { id: order.id })}
            </h3>
            {/* Date and Status */}
            <div className="flex items-start justify-between gap-2">
              <div className="text-muted-foreground space-y-0.5 text-xs">
                <p>
                  {t("detail.labels.orderTime")}: {orderTime}
                </p>
                {showEstimatedDelivery && (
                  <p>
                    {t("detail.labels.estimatedDelivery")}:{" "}
                    {estimatedDeliveryTime}
                  </p>
                )}
              </div>
              <div className="flex-shrink-0">
                {getStatusBadge(order.status)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Business Info */}
      {order.business && (
        <div className="mb-2.5">
          <div className="flex items-center space-x-2">
            {order.business.logo ? (
              <img
                src={order.business.logo}
                alt={order.business.name || t("card.business")}
                className="h-5 w-5 flex-shrink-0 rounded object-cover"
              />
            ) : (
              <Icon
                name="Store"
                className="text-muted-foreground h-5 w-5 flex-shrink-0"
              />
            )}
            <div className="min-w-0 flex-1">
              <span className="text-foreground block truncate text-xs font-medium sm:text-sm">
                {order.business.name || t("common.notAvailable")}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Customer Info */}
      <div className="mb-3">
        <div className="flex items-start space-x-2">
          <Icon
            name="User"
            className="text-muted-foreground h-5 w-5 flex-shrink-0"
          />
          <div className="min-w-0 flex-1">
            <span className="text-foreground block truncate text-xs font-medium sm:text-sm">
              {getCustomerName(order)}
            </span>
            <span className="text-muted-foreground block truncate text-xs">
              {getCustomerPhone(order)}
            </span>
            <div className="mt-0.5 flex items-start space-x-1.5">
              <Icon
                name="MapPin"
                className="text-muted-foreground mt-0.5 h-3 w-3 flex-shrink-0"
              />
              <span className="text-muted-foreground line-clamp-1 text-xs">
                {order?.customer?.address || t("common.notAvailable")}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Order Items - Fixed height with scroll */}
      <div className="mb-3 min-h-0 flex-1">
        <div className="max-h-24 space-y-1 overflow-y-auto sm:max-h-32 sm:space-y-1.5">
          {(order.products || []).map((product, index) => (
            <div
              key={product.id || index}
              className="bg-card rounded-lg p-1.5 sm:p-2"
            >
              {/* Product Header */}
              <div className="flex items-start justify-between">
                <div className="min-w-0 flex-1">
                  <h4 className="text-foreground truncate text-xs font-medium">
                    {t("detail.labels.productWithQuantity", {
                      quantity: product.quantity,
                      name: product.name,
                    })}
                  </h4>
                </div>
                <div className="ml-2 flex-shrink-0 text-right">
                  <div className="text-foreground text-xs font-medium">
                    {parsePrice(product?.total || product?.summary?.total) ||
                      "0.00"}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer - Always at bottom */}
      <div className="mt-auto">
        {/* Total */}
        <div className="border-border mb-2 border-t pt-2">
          <div className="flex items-center justify-between">
            <span className="text-foreground text-xs font-semibold sm:text-sm">
              {t("card.total")}
            </span>
            <span className="text-primary font-bold sm:text-base">
              {parsePrice(order.summary?.total || 0)}
            </span>
          </div>
        </div>

        {/* Instructions */}
        {order.instructions && (
          <div className="bg-card mb-2 rounded-lg p-1.5 sm:p-2">
            <p className="text-foreground line-clamp-1 text-xs">
              <strong>{t("card.instructions")}:</strong> {order.instructions}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="grid grid-cols-2 gap-2 pt-1">
          <Button
            variant="ghost"
            onClick={() => onViewDetails(order)}
            className="border-border bg-card text-foreground hover:border-border hover:bg-accent hover:text-foreground flex h-8 items-center justify-center gap-1.5 rounded-md border text-xs font-medium transition-colors"
          >
            <Icon name="FileText" className="h-3.5 w-3.5" />
            {t("card.actions.details")}
          </Button>
          <HoverCard openDelay={200} closeDelay={100}>
            <HoverCardTrigger asChild>
              <Button
                variant="ghost"
                onClick={
                  order?.conversation_id
                    ? () => onViewConversation(order)
                    : null
                }
                className={`flex h-8 items-center justify-center gap-1.5 rounded-md border text-xs font-medium transition-colors ${
                  order?.conversation_id
                    ? "border-primary/40 bg-primary text-primary-foreground hover:bg-primary"
                    : "border-border bg-card text-muted-foreground cursor-not-allowed"
                }`}
                disabled={!order?.conversation_id}
              >
                <Icon
                  name="MessageCircle"
                  className={`h-3.5 w-3.5 ${
                    order?.conversation_id
                      ? "text-primary-foreground"
                      : "text-muted-foreground"
                  }`}
                />
                {t("card.actions.chat")}
              </Button>
            </HoverCardTrigger>
            {!order?.conversation_id && (
              <HoverCardContent side="top" align="end" className="w-64">
                <HoverCardArrow />
                <div className="flex items-start space-x-2">
                  <Icon
                    name="Info"
                    className="text-primary mt-0.5 h-4 w-4 flex-shrink-0"
                  />
                  <div>
                    <p className="text-foreground mb-1 text-sm font-medium">
                      {t("card.chatUnavailable.title")}
                    </p>
                    <p className="text-muted-foreground text-xs">
                      {t("card.chatUnavailable.description")}
                    </p>
                  </div>
                </div>
              </HoverCardContent>
            )}
          </HoverCard>
        </div>
      </div>
    </div>
  );
};

export default OrderCard;
